import { useState } from 'react'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'
import { alertSourceOf, isBatchEvent } from '../utils/alertSources'
import { useAlertRecipients } from './useAlerts'
import type { AlertEventType, AlertRule, AlertSourceKind, AutoCoderPipeline } from '../types/gotypes'

export type AlertFormState = { mode: 'create' } | { mode: 'edit'; alert: AlertRule }

/** A pipeline an import-batch event alert can be narrowed to. */
export interface AlertFormPipeline {
  id: string
  name: string
}

/**
 * The "Watch an event" / Edit alert dialog's page-side state: whether it is
 * open, for which alert, an event alert's job and event, and what those need
 * fetched: the job's AutoCoder pipelines for an import-batch event alert
 * (GET /autocoder/:customer/:job/pipelines, open to the customer's members)
 * and the colleagues "Also notify" can name (GET /alerts/:customer/recipients).
 * A table or question alert's tables and condition change on the agent start
 * screen, not here, so the dialog needs no TOC.
 */
export function useAlertForm(customer: string) {
  const [form, setForm] = useState<AlertFormState | null>(null)
  // Bumped on every open, so the dialog starts from the alert (or blank) each time.
  const [openCount, setOpenCount] = useState(0)
  const [job, setJob] = useState('')
  const [kind, setKind] = useState<AlertSourceKind>('table')
  const [eventType, setEventType] = useState<AlertEventType | ''>('')
  // The server's reason the last create or save failed, shown in the dialog.
  const [error, setError] = useState<string | null>(null)
  const [recipientQuery, setRecipientQuery] = useState('')

  const recipients = useAlertRecipients({ customer, q: recipientQuery, enabled: Boolean(form) })

  const pipelinesQuery = useQuery({
    queryKey: ['alert-form-pipelines', customer, job],
    enabled: Boolean(form && customer && job && kind === 'event' && isBatchEvent(eventType)),
    queryFn: async (): Promise<AlertFormPipeline[]> => {
      const res = await axios.get<{ items: AutoCoderPipeline[] }>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines`,
        { params: { limit: 500, offset: 0, lifecycle: 'any' } },
      )
      // A pipeline's display name lives on its config; system_name is the
      // connector's handle and the only name a scaffolded pipeline has.
      return (res.data?.items || []).map((p) => ({ id: p.id, name: p.config?.name || p.system_name || p.id }))
    },
  })

  /** "Watch an event": the only alert the dialog makes (tables and questions are made with the agent). */
  const handleOpenCreate = (defaultJob: string) => {
    setJob(defaultJob)
    setKind('event')
    setEventType('')
    setError(null)
    setRecipientQuery('')
    setForm({ mode: 'create' })
    setOpenCount((n) => n + 1)
  }

  const handleOpenEdit = (alert: AlertRule) => {
    const source = alertSourceOf(alert)
    setJob(source.job)
    setKind(source.kind)
    setEventType(source.event?.type ?? '')
    setError(null)
    setRecipientQuery('')
    setForm({ mode: 'edit', alert })
    setOpenCount((n) => n + 1)
  }

  const handleClose = () => {
    setForm(null)
    setError(null)
  }

  return {
    form,
    formKey: `${form?.mode === 'edit' ? form.alert.id : 'new'}-${openCount}`,
    job,
    handleJobChange: setJob,
    kind,
    eventType,
    handleEventTypeChange: setEventType,
    pipelines: pipelinesQuery.data ?? [],
    pipelinesLoading: pipelinesQuery.isLoading,
    recipientOptions: recipients.options,
    recipientsLoading: recipients.loading,
    recipientsError: recipients.error,
    recipientQuery,
    handleRecipientQueryChange: setRecipientQuery,
    error,
    handleError: setError,
    handleOpenCreate,
    handleOpenEdit,
    handleClose,
  }
}

export default useAlertForm
